import * as React from 'react';
import styled from 'styled-components';

import { Dialog } from '@longline/aqua-ui/services/Dialog';
import { SecondaryButton } from '@longline/aqua-ui/controls/SecondaryButton';
import { TertiaryButton } from '@longline/aqua-ui/controls/TertiaryButton';
import { SVG } from '@longline/aqua-ui/svg';

import { ChartRenderer, ChartRef } from './ChartRenderer';
import type { IAxisConfig, ISeriesConfig } from './types';

interface IProps {
  /** @ignore */
  className?: string;

  /**
   * Total number of days to display on the x-axis.
   * The axis will range from 0 to (days - 1).
   */
  days: number;

  /**
   * Array of series configurations.
   * Each series defines a line with its own data, color, and Y-axis.
   * Multiple series can share the same axis position (left/right).
   */
  series: ISeriesConfig[];
}

/**
 * A multi-series line chart component for displaying growth metrics over time.
 *
 * GrowthChart is designed for aquaculture simulation results, showing metrics
 * like weight, length, and feed conversion ratio over a culture period. It
 * supports:
 *
 * - **Multiple Y-axes**: Each series can have its own axis with independent
 *   scale, positioned on the left or right side of the chart.
 *
 * - **Nice tick values**: The X-axis automatically calculates "nice" tick
 *   intervals (0, 100, 200, etc.) based on the chart width.
 *
 * - **Zoom dialog**: A magnifying glass button opens the chart in a full-width
 *   modal dialog for detailed inspection.
 *
 * - **Early data hiding**: Series can hide initial days where values are
 *   unstable (e.g., FCR before day 10).
 *
 * @example
 * ```tsx
 * <GrowthChart
 *   days={500}
 *   series={[
 *     {
 *       label: 'Weight (g)',
 *       color: 'red',
 *       data: weightData,
 *       axis: { position: 'left', title: 'Weight (g)', min: 0, width: 60 }
 *     },
 *     {
 *       label: 'Length (cm)',
 *       color: 'green',
 *       data: lengthData,
 *       axis: { position: 'right', title: 'Length (cm)', min: 0, width: 42 }
 *     }
 *   ]}
 * />
 * ```
 */
const GrowthChartBase = (props: IProps) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const chartRef = React.useRef<ChartRef | null>(null);

  const handleExport = () => {
    if (chartRef.current) {
      const chart = chartRef.current;
      const originalWidth = chart.width;
      const originalHeight = chart.height;

      // Resize to 2x for higher resolution export
      chart.resize(originalWidth * 2, originalHeight * 2);

      const url = chart.toBase64Image();
      const link = document.createElement('a');
      link.download = 'chart.png';
      link.href = url;
      link.click();

      // Restore original size
      chart.resize(originalWidth, originalHeight);
    }
  };

  return (
    <ChartContainer className={props.className}>
      <ChartRenderer
        days={props.days}
        series={props.series}
        height={200}
      />
      <div style={{height: '16px'}}></div>

      <ZoomButton
        color="white"
        icon={{ color: '#333', url: SVG.Icons.Search }}
        title="Enlarge chart"
        onClick={() => setDialogOpen(true)}
      />

      <Dialog
        open={dialogOpen}
        width="90vw"
        inverted
        onClose={() => setDialogOpen(false)}
      >
        <Dialog.Content>
          <div style={{ height: '16px' }}></div>
          <ChartRenderer
            ref={chartRef}
            days={props.days}
            series={props.series}
            height="65vh"
            textColor="white"
            gridColor="rgba(255, 255, 255, 0.15)"
            lineWidth={2}
          />
        </Dialog.Content>
        <Dialog.Footer align="space-between">
          <TertiaryButton onClick={handleExport}>EXPORT PNG</TertiaryButton>
          <TertiaryButton onClick={() => setDialogOpen(false)}>CLOSE</TertiaryButton>
        </Dialog.Footer>
      </Dialog>
    </ChartContainer>
  )
}

const ChartContainer = styled.div`
  position: relative;
`

const ZoomButton = styled(SecondaryButton)`
  position: absolute;
  bottom: 16px;
  right: 0;
`

const GrowthChart = styled(GrowthChartBase)``

export { GrowthChart }
export type { ISeriesConfig, IAxisConfig }
